'use client';

import { c as _c } from "react/compiler-runtime";
import type { BigIntish, MarketId, MarketParams } from '@morpho-org/blue-sdk';
import type { InterfaceToken, MarketForRewards } from '@morpho-org/dapp-core';
import type { Address } from 'viem';
import { ZERO } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { LargeAssetInput } from '@morpho-org/ui-v6/client';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { useQueryClient } from '@tanstack/react-query';
import { memo, useCallback, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import type { MarketGeoRestriction } from '@/hooks/useMarketGeoRestriction';
import { ActionButtons } from '@/components/common/ActionButtons';
import BundleAuthorizationCallout from '@/components/common/BundleAuthorizationCallout/BundleAuthorizationCallout';
import { MARKET_BUNDLE_NOT_AUTHORIZED_MESSAGE } from '@/components/common/BundleAuthorizationCallout/messages';
import { ButtonLink } from '@/components/common/ButtonLink';
import { ErrorServiceUnavailable } from '@/components/common/Error';
import { TransferError } from '@/components/common/Error/TransferErrors';
import { WrapSegment } from '@/components/common/WrapSegment';
import MarketActionReviewDialogV2 from '@/components/MarketPage/MarketActionV2/MarketActionReviewDialogV2';
import { useWeb3Context } from '@/contexts/Web3Context';
import { getMarketGeoRestrictionActionProps } from '@/helpers/marketGeoRestriction';
import { getSupplyBorrowOperationLabel } from '@/helpers/operationLabels';
import { getVaultUrl } from '@/helpers/routing';
import useMarketTooltip from '@/hooks/operation/market/useMarketTooltip';
import { computeLtv, computePreviewFinalPosition } from '@/hooks/operation/market/v2/marketAction.helpers';
import { getMarketActionError } from '@/hooks/operation/market/v2/marketActionError';
import { useMarketActionBuilder } from '@/hooks/operation/market/v2/useMarketActionBuilder';
import { useMarketBlue } from '@/hooks/operation/market/v2/useMarketBlue';
import { useMarketCollateralNativeWrap } from '@/hooks/operation/market/v2/useMarketCollateralNativeWrap';
import { useMarketMaxBorrowV2 } from '@/hooks/operation/market/v2/useMarketMaxBorrowV2';
import { useMarketReallocations } from '@/hooks/operation/market/v2/useMarketReallocations';
import useAssetCanTransferOnchain from '@/hooks/operation/useAssetCanTransferOnchain';
import useBundleCanTransferOnchain, { isMarketBundleBlocked } from '@/hooks/operation/useBundleCanTransferOnchain';
import useFinalizeLabel from '@/hooks/operation/useFinalizeLabel';
import { useStickyMaxInput } from '@/hooks/useStickyMaxInput';
import { useTrackMarketActionsV2EntryViewed } from '@/lib/analytics/client/hooks/useTrackMarketActionsV2EntryViewed';
import { trackMarketActionsV2EntryClicked } from '@/lib/analytics/client/trackers/market-actions-v2';
import { buildMarketBorrowCompletedParams, trackMarketBorrowCompleted } from '@/lib/analytics/client/trackers/market-borrow';
import { getWalletType } from '@/lib/observability/shared/wallet-type';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
import { MarketSummaryCard } from '../MarketSummary';

/** Debounce window (ms) before an edited amount drives the local build (validation only). */
const PREVIEW_DEBOUNCE_MS = 400;
interface MarketOperationSupplyBorrowProps {
  marketId: MarketId;
  collateralAsset: InterfaceToken;
  loanAsset: InterfaceToken;
  lltv: BigIntish | undefined;
  /** morpho-sdk MarketParams for the v2 flow (from the parent's API market). */
  marketParams: MarketParams | undefined;
  collateralVault?: {
    name: string;
    address: Address;
  } | null;
  marketForRewards?: MarketForRewards | null;
  avgBorrowApy?: number | null;
  /** VRM-5363: restricted-jurisdiction verdict for this market's assets; blocks the CTA. */
  geoRestriction: MarketGeoRestriction;
  /**
   * The panel's one-sided gate has not answered yet (e.g. the reads restarting on connect).
   * The form still renders so typed input survives, but it cannot be finalized: the wallet
   * could turn out to hold a lend position, which bars a borrow.
   */
  isPositionGatePending: boolean;
}
const MarketOperationSupplyBorrow = t0 => {
  const $ = _c(165);
  const {
    marketId,
    collateralAsset,
    loanAsset,
    lltv,
    marketParams,
    collateralVault,
    marketForRewards,
    avgBorrowApy,
    geoRestriction,
    isPositionGatePending
  } = t0;
  const {
    account,
    targetChain,
    targetChainIdentifier
  } = useWeb3Context();
  const {
    toggleDialog
  } = useDialogContext();
  const {
    marketSupplyBorrow
  } = useFeatureFlags();
  const queryClient = useQueryClient();
  const [inputCollateralAmount, setInputCollateralAmount] = useState(ZERO);
  const [inputLoanAmount, setInputLoanAmount] = useState(ZERO);
  const [isCollateralInputPending, setIsCollateralInputPending] = useState(false);
  const [isLoanInputPending, setIsLoanInputPending] = useState(false);
  const isMarketSupplyBorrowEnabled = marketSupplyBorrow && !!marketParams;
  let t1;
  if ($[0] !== isMarketSupplyBorrowEnabled || $[1] !== marketId || $[2] !== targetChain.id || $[3] !== targetChain.name) {
    t1 = {
      entryPoint: "market_page",
      action: "supply_borrow",
      marketId,
      chainId: targetChain.id,
      chainName: targetChain.name,
      enabled: isMarketSupplyBorrowEnabled
    };
    $[0] = isMarketSupplyBorrowEnabled;
    $[1] = marketId;
    $[2] = targetChain.id;
    $[3] = targetChain.name;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  useTrackMarketActionsV2EntryViewed(t1);
  const {
    positionData,
    error: positionError,
    isLoading: isPositionLoading
  } = useMarketBlue(marketParams);
  let t2;
  if ($[5] !== inputLoanAmount || $[6] !== isMarketSupplyBorrowEnabled || $[7] !== marketParams) {
    t2 = {
      marketParams,
      operation: "borrow",
      amount: inputLoanAmount,
      enabled: isMarketSupplyBorrowEnabled
    };
    $[5] = inputLoanAmount;
    $[6] = isMarketSupplyBorrowEnabled;
    $[7] = marketParams;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const {
    reallocations,
    maxReallocatable,
    isLoading: isReallocationLoading
  } = useMarketReallocations(t2);
  let t3;
  if ($[9] !== collateralAsset.address || $[10] !== inputCollateralAmount || $[11] !== isMarketSupplyBorrowEnabled) {
    t3 = {
      collateralToken: collateralAsset.address,
      totalCollateral: inputCollateralAmount,
      enabled: isMarketSupplyBorrowEnabled,
      wrapEnabled: isMarketSupplyBorrowEnabled
    };
    $[9] = collateralAsset.address;
    $[10] = inputCollateralAmount;
    $[11] = isMarketSupplyBorrowEnabled;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const {
    isWNativeCollateral,
    isWrapping,
    onWrappingChange,
    wrappableNativeBalance,
    nativeAmount,
    maxCollateral,
    isMaxLoading: isMaxCollateralLoading,
    isWrappingToggleLoading,
    wrapErrorMessage
  } = useMarketCollateralNativeWrap(t3);
  let t4;
  if ($[13] !== maxCollateral) {
    t4 = {
      maxValue: maxCollateral,
      onChange: setInputCollateralAmount
    };
    $[13] = maxCollateral;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const {
    isMaxActive: isCollateralMaxActive,
    onMaxClick: onMaxCollateralClick,
    onManualChange: setInputCollateralAmountManually
  } = useStickyMaxInput(t4);
  let t5;
  if ($[15] !== inputCollateralAmount || $[16] !== isMarketSupplyBorrowEnabled || $[17] !== maxCollateral) {
    t5 = isMarketSupplyBorrowEnabled && isDefined(maxCollateral) && inputCollateralAmount > maxCollateral;
    $[15] = inputCollateralAmount;
    $[16] = isMarketSupplyBorrowEnabled;
    $[17] = maxCollateral;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const isCollateralOverMax = t5;
  let t6;
  if ($[19] !== inputCollateralAmount || $[20] !== isMarketSupplyBorrowEnabled || $[21] !== marketParams || $[22] !== maxReallocatable) {
    t6 = {
      marketParams,
      proposedCollateral: inputCollateralAmount,
      maxReallocatable,
      enabled: isMarketSupplyBorrowEnabled
    };
    $[19] = inputCollateralAmount;
    $[20] = isMarketSupplyBorrowEnabled;
    $[21] = marketParams;
    $[22] = maxReallocatable;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const {
    maxBorrow,
    maxBorrowCeiling,
    isMaxLoading: isMaxBorrowLoading
  } = useMarketMaxBorrowV2(t6);
  let t7;
  if ($[24] !== maxBorrow) {
    t7 = {
      maxValue: maxBorrow,
      onChange: setInputLoanAmount
    };
    $[24] = maxBorrow;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  const {
    isMaxActive: isBorrowMaxActive,
    onMaxClick: onMaxBorrowClick,
    onManualChange: setInputLoanAmountManually
  } = useStickyMaxInput(t7);
  let t8;
  if ($[26] !== inputLoanAmount || $[27] !== isMarketSupplyBorrowEnabled || $[28] !== maxBorrowCeiling) {
    t8 = isMarketSupplyBorrowEnabled && isDefined(maxBorrowCeiling) && inputLoanAmount > maxBorrowCeiling;
    $[26] = inputLoanAmount;
    $[27] = isMarketSupplyBorrowEnabled;
    $[28] = maxBorrowCeiling;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  const isBorrowOverMax = t8;
  const [debouncedCollateral] = useDebounce(inputCollateralAmount, PREVIEW_DEBOUNCE_MS);
  const [debouncedLoan] = useDebounce(inputLoanAmount, PREVIEW_DEBOUNCE_MS);
  const isDebouncing = debouncedCollateral !== inputCollateralAmount || debouncedLoan !== inputLoanAmount;
  let t9;
  if ($[30] !== debouncedCollateral || $[31] !== debouncedLoan || $[32] !== marketParams || $[33] !== nativeAmount || $[34] !== reallocations) {
    t9 = {
      marketParams,
      mode: "supplyBorrow",
      collateralAmount: debouncedCollateral,
      loanAmount: debouncedLoan,
      nativeAmount,
      reallocations,
      withRequirements: false
    };
    $[30] = debouncedCollateral;
    $[31] = debouncedLoan;
    $[32] = marketParams;
    $[33] = nativeAmount;
    $[34] = reallocations;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  const {
    buildError
  } = useMarketActionBuilder(t9);
  let t10;
  if ($[36] !== positionData) {
    t10 = positionData ? {
      collateral: positionData.collateral,
      borrowAssets: positionData.borrowAssets,
      price: positionData.market.price ?? ZERO
    } : undefined;
    $[36] = positionData;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  const current = t10;
  const isEmpty = inputCollateralAmount === ZERO && inputLoanAmount === ZERO;
  let t11;
  bb0: {
    if (isEmpty || !current) {
      let t12;
      if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
        t12 = {};
        $[38] = t12;
      } else {
        t12 = $[38];
      }
      t11 = t12;
      break bb0;
    }
    let t12;
    if ($[39] !== current || $[40] !== inputCollateralAmount || $[41] !== inputLoanAmount) {
      t12 = computePreviewFinalPosition({
        current,
        collateralDelta: inputCollateralAmount,
        loanDelta: inputLoanAmount,
        mode: "supplyBorrow"
      });
      $[39] = current;
      $[40] = inputCollateralAmount;
      $[41] = inputLoanAmount;
      $[42] = t12;
    } else {
      t12 = $[42];
    }
    t11 = t12;
  }
  const after = t11;
  let t12;
  bb1: {
    if (wrapErrorMessage) {
      t12 = wrapErrorMessage;
      break bb1;
    }
    if (isBorrowOverMax) {
      t12 = "Maximum borrowable exceeded";
      break bb1;
    }
    let t13;
    if ($[43] !== buildError || $[44] !== collateralAsset.symbol || $[45] !== isCollateralOverMax || $[46] !== isDebouncing || $[47] !== isEmpty) {
      t13 = isEmpty || isDebouncing ? undefined : getMarketActionError({
        buildError,
        insufficientBalanceSymbol: isCollateralOverMax ? collateralAsset.symbol : undefined
      });
      $[43] = buildError;
      $[44] = collateralAsset.symbol;
      $[45] = isCollateralOverMax;
      $[46] = isDebouncing;
      $[47] = isEmpty;
      $[48] = t13;
    } else {
      t13 = $[48];
    }
    t12 = t13;
  }
  const buildErrorMessage = t12;
  const canTransferCollateralAsset = useAssetCanTransferOnchain(account.address, collateralAsset.address, targetChain.id);
  const canTransferLoanAsset = useAssetCanTransferOnchain(account.address, loanAsset.address, targetChain.id);
  const {
    canTransfer: collateralBundleAuthorized
  } = useBundleCanTransferOnchain("bundles.blueBundlesV1", collateralAsset.address, targetChain.id);
  const {
    canTransfer: loanBundleAuthorized
  } = useBundleCanTransferOnchain("bundles.blueBundlesV1", loanAsset.address, targetChain.id);
  let t13;
  if ($[49] !== collateralBundleAuthorized || $[50] !== inputCollateralAmount || $[51] !== inputLoanAmount || $[52] !== loanBundleAuthorized) {
    t13 = isMarketBundleBlocked({
      legs: [{
        amount: inputCollateralAmount,
        authorized: collateralBundleAuthorized
      }, {
        amount: inputLoanAmount,
        authorized: loanBundleAuthorized
      }]
    });
    $[49] = collateralBundleAuthorized;
    $[50] = inputCollateralAmount;
    $[51] = inputLoanAmount;
    $[52] = loanBundleAuthorized;
    $[53] = t13;
  } else {
    t13 = $[53];
  }
  const isBundleNotAuthorized = t13;
  const isBorrowReallocationLoading = inputLoanAmount > ZERO && isReallocationLoading;
  const isFinalizeDisabled = !isMarketSupplyBorrowEnabled || isPositionGatePending || isEmpty || isPositionLoading || !!positionError || inputCollateralAmount > ZERO && isMaxCollateralLoading || isMaxBorrowLoading || isBorrowReallocationLoading || account.isWrongChain || !!buildErrorMessage || isCollateralOverMax || isBorrowOverMax || !canTransferCollateralAsset || !canTransferLoanAsset || isBundleNotAuthorized;
  let t14;
  if ($[54] !== inputCollateralAmount || $[55] !== inputLoanAmount) {
    t14 = getSupplyBorrowOperationLabel(inputCollateralAmount, inputLoanAmount);
    $[54] = inputCollateralAmount;
    $[55] = inputLoanAmount;
    $[56] = t14;
  } else {
    t14 = $[56];
  }
  const operationLabel = t14;
  let t15;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = [];
    $[57] = t15;
  } else {
    t15 = $[57];
  }
  let t16;
  if ($[58] !== account.isWrongChain || $[59] !== buildErrorMessage || $[60] !== isEmpty || $[61] !== operationLabel) {
    t16 = {
      operationLabel,
      isInitialState: isEmpty,
      isNew: true,
      isEditing: false,
      localErrorMessage: buildErrorMessage,
      operations: t15,
      isPending: false,
      blockError: null,
      isWrongChain: account.isWrongChain,
      hasDisabledMessage: false,
      noReviewPrefix: true
    };
    $[58] = account.isWrongChain;
    $[59] = buildErrorMessage;
    $[60] = isEmpty;
    $[61] = operationLabel;
    $[62] = t16;
  } else {
    t16 = $[62];
  }
  const finalizeLabel = useFinalizeLabel(t16);
  const tooltipContent = useMarketTooltip(buildErrorMessage);
  const isButtonLoading = account.isConnected && !positionError && (isPositionLoading || isBorrowReallocationLoading);
  let t17;
  if ($[63] !== finalizeLabel || $[64] !== geoRestriction || $[65] !== isButtonLoading || $[66] !== isFinalizeDisabled || $[67] !== tooltipContent) {
    t17 = getMarketGeoRestrictionActionProps({
      geoRestriction,
      isFinalizeDisabled,
      isLoading: isButtonLoading,
      finalizeLabel,
      tooltipContent
    });
    $[63] = finalizeLabel;
    $[64] = geoRestriction;
    $[65] = isButtonLoading;
    $[66] = isFinalizeDisabled;
    $[67] = tooltipContent;
    $[68] = t17;
  } else {
    t17 = $[68];
  }
  let geoActionProps;
  let shouldBlockFinalize;
  if ($[69] !== t17) {
    ({
      shouldBlockFinalize,
      ...geoActionProps
    } = t17);
    $[69] = t17;
    $[70] = geoActionProps;
    $[71] = shouldBlockFinalize;
  } else {
    geoActionProps = $[70];
    shouldBlockFinalize = $[71];
  }
  let t18;
  if ($[72] !== account.isContract || $[73] !== account.isSafe || $[74] !== avgBorrowApy || $[75] !== collateralAsset || $[76] !== inputCollateralAmount || $[77] !== inputLoanAmount || $[78] !== loanAsset || $[79] !== marketForRewards || $[80] !== marketId || $[81] !== marketParams || $[82] !== nativeAmount || $[83] !== queryClient || $[84] !== reallocations || $[85] !== shouldBlockFinalize || $[86] !== targetChain.id || $[87] !== targetChain.name || $[88] !== toggleDialog) {
    t18 = () => {
      if (!marketParams || shouldBlockFinalize) {
        return;
      }
      trackMarketActionsV2EntryClicked({
        entryPoint: "market_page",
        action: "supply_borrow",
        marketId,
        chainId: targetChain.id,
        chainName: targetChain.name
      });
      toggleDialog(<MarketActionReviewDialogV2 marketParams={marketParams} marketId={marketId} mode="supplyBorrow" collateralAmount={inputCollateralAmount} loanAmount={inputLoanAmount} nativeAmount={nativeAmount} reallocations={reallocations} collateralAsset={collateralAsset} loanAsset={loanAsset} lltv={marketParams.lltv} marketForRewards={marketForRewards} avgBorrowApy={avgBorrowApy} onTxSuccess={() => {
        const params = buildMarketBorrowCompletedParams({
          marketId,
          loanAsset,
          collateralAsset,
          loanAmount: inputLoanAmount,
          collateralAmount: inputCollateralAmount,
          chainId: targetChain.id,
          chainName: targetChain.name,
          walletType: getWalletType(account.isSafe, account.isContract),
          entryPoint: "market_page"
        });
        if (params) {
          trackMarketBorrowCompleted(params);
        }
        setInputCollateralAmount(ZERO);
        setInputLoanAmount(ZERO);
        queryClient.invalidateQueries();
      }} entryPoint="market_page" />);
    };
    $[72] = account.isContract;
    $[73] = account.isSafe;
    $[74] = avgBorrowApy;
    $[75] = collateralAsset;
    $[76] = inputCollateralAmount;
    $[77] = inputLoanAmount;
    $[78] = loanAsset;
    $[79] = marketForRewards;
    $[80] = marketId;
    $[81] = marketParams;
    $[82] = nativeAmount;
    $[83] = queryClient;
    $[84] = reallocations;
    $[85] = shouldBlockFinalize;
    $[86] = targetChain.id;
    $[87] = targetChain.name;
    $[88] = toggleDialog;
    $[89] = t18;
  } else {
    t18 = $[89];
  }
  const handleFinalize = t18;
  let t19;
  bb2: {
    if (!collateralVault) {
      t19 = null;
      break bb2;
    }
    t19 = `Deposit liquidity into ${collateralVault.name} to use it as collateral in this market.`;
  }
  const collateralVaultButtonLabel = t19;
  if (positionError && !positionData) {
    let t20;
    if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = <ErrorServiceUnavailable minHeight="600px" />;
      $[90] = t20;
    } else {
      t20 = $[90];
    }
    return t20;
  }
  const t20 = `Supply Collateral ${collateralAsset.symbol}`;
  let t21;
  if ($[91] !== collateralVault || $[92] !== collateralVaultButtonLabel || $[93] !== targetChainIdentifier) {
    t21 = collateralVaultButtonLabel && collateralVault && <ButtonLink href={getVaultUrl({
      identifier: targetChainIdentifier,
      vaultAddress: collateralVault.address,
      vaultName: collateralVault.name
    })} mode="external" buttonProps={{
      children: <span className="line-clamp-3 text-start leading-normal whitespace-normal">{collateralVaultButtonLabel}</span>,
      variant: "secondary",
      size: "sm",
      style: {
        padding: "10px",
        borderRadius: "8px",
        height: "56px",
        gap: "2px"
      }
    }} />;
    $[91] = collateralVault;
    $[92] = collateralVaultButtonLabel;
    $[93] = targetChainIdentifier;
    $[94] = t21;
  } else {
    t21 = $[94];
  }
  let t22;
  if ($[95] !== collateralAsset || $[96] !== inputCollateralAmount || $[97] !== isCollateralMaxActive || $[98] !== isMaxCollateralLoading || $[99] !== maxCollateral || $[100] !== onMaxCollateralClick || $[101] !== setInputCollateralAmountManually || $[102] !== t20 || $[103] !== t21) {
    t22 = <LargeAssetInput header={t20} token={collateralAsset} amount={inputCollateralAmount} onChange={setInputCollateralAmountManually} onPendingChange={setIsCollateralInputPending} maxValue={maxCollateral} isMaxLoading={isMaxCollateralLoading} isMax={isCollateralMaxActive} onMaxClick={onMaxCollateralClick} autoFocus={true}>{t21}</LargeAssetInput>;
    $[95] = collateralAsset;
    $[96] = inputCollateralAmount;
    $[97] = isCollateralMaxActive;
    $[98] = isMaxCollateralLoading;
    $[99] = maxCollateral;
    $[100] = onMaxCollateralClick;
    $[101] = setInputCollateralAmountManually;
    $[102] = t20;
    $[103] = t21;
    $[104] = t22;
  } else {
    t22 = $[104];
  }
  let t23;
  if ($[105] !== collateralAsset.symbol || $[106] !== isMarketSupplyBorrowEnabled || $[107] !== isWNativeCollateral || $[108] !== isWrapping || $[109] !== isWrappingToggleLoading || $[110] !== onWrappingChange || $[111] !== targetChain.nativeCurrency.symbol || $[112] !== wrappableNativeBalance) {
    t23 = <WrapSegment id="market-supply" enabled={isMarketSupplyBorrowEnabled} isWNative={isWNativeCollateral} wrappableNativeBalance={wrappableNativeBalance} wrappedSymbol={collateralAsset.symbol} nativeSymbol={targetChain.nativeCurrency.symbol} isWrapping={isWrapping} onWrappingChange={onWrappingChange} disabled={isWrappingToggleLoading} />;
    $[105] = collateralAsset.symbol;
    $[106] = isMarketSupplyBorrowEnabled;
    $[107] = isWNativeCollateral;
    $[108] = isWrapping;
    $[109] = isWrappingToggleLoading;
    $[110] = onWrappingChange;
    $[111] = targetChain.nativeCurrency.symbol;
    $[112] = wrappableNativeBalance;
    $[113] = t23;
  } else {
    t23 = $[113];
  }
  let t24;
  if ($[114] !== canTransferCollateralAsset || $[115] !== collateralAsset.address) {
    t24 = !canTransferCollateralAsset && <TransferError tokenAddress={collateralAsset.address} />;
    $[114] = canTransferCollateralAsset;
    $[115] = collateralAsset.address;
    $[116] = t24;
  } else {
    t24 = $[116];
  }
  let t25;
  if ($[117] !== t22 || $[118] !== t23 || $[119] !== t24) {
    t25 = <div className="flex flex-col gap-[2px]">{t22}{t23}{t24}</div>;
    $[117] = t22;
    $[118] = t23;
    $[119] = t24;
    $[120] = t25;
  } else {
    t25 = $[120];
  }
  const t26 = `Borrow ${loanAsset.symbol}`;
  let t27;
  if ($[121] !== inputLoanAmount || $[122] !== isBorrowMaxActive || $[123] !== isMaxBorrowLoading || $[124] !== loanAsset || $[125] !== maxBorrow || $[126] !== onMaxBorrowClick || $[127] !== setInputLoanAmountManually || $[128] !== t26) {
    t27 = <LargeAssetInput header={t26} token={loanAsset} amount={inputLoanAmount} onChange={setInputLoanAmountManually} onPendingChange={setIsLoanInputPending} maxValue={maxBorrow} isMaxLoading={isMaxBorrowLoading} isMax={isBorrowMaxActive} onMaxClick={onMaxBorrowClick} />;
    $[121] = inputLoanAmount;
    $[122] = isBorrowMaxActive;
    $[123] = isMaxBorrowLoading;
    $[124] = loanAsset;
    $[125] = maxBorrow;
    $[126] = onMaxBorrowClick;
    $[127] = setInputLoanAmountManually;
    $[128] = t26;
    $[129] = t27;
  } else {
    t27 = $[129];
  }
  let t28;
  if ($[130] !== canTransferLoanAsset || $[131] !== loanAsset.address) {
    t28 = !canTransferLoanAsset && <TransferError tokenAddress={loanAsset.address} />;
    $[130] = canTransferLoanAsset;
    $[131] = loanAsset.address;
    $[132] = t28;
  } else {
    t28 = $[132];
  }
  let t29;
  if ($[133] !== t27 || $[134] !== t28) {
    t29 = <div className="flex flex-col gap-[2px]">{t27}{t28}</div>;
    $[133] = t27;
    $[134] = t28;
    $[135] = t29;
  } else {
    t29 = $[135];
  }
  const t30 = current?.collateral;
  const t31 = current?.borrowAssets;
  let t32;
  if ($[136] !== current) {
    t32 = current ? computeLtv({
      collateral: current.collateral,
      borrowAssets: current.borrowAssets,
      price: current.price
    }) : undefined;
    $[136] = current;
    $[137] = t32;
  } else {
    t32 = $[137];
  }
  let t33;
  if ($[138] !== t30 || $[139] !== t31 || $[140] !== t32) {
    t33 = {
      collateral: t30,
      loan: t31,
      ltv: t32
    };
    $[138] = t30;
    $[139] = t31;
    $[140] = t32;
    $[141] = t33;
  } else {
    t33 = $[141];
  }
  let t34;
  if ($[142] !== after || $[143] !== avgBorrowApy || $[144] !== collateralAsset || $[145] !== isPositionLoading || $[146] !== lltv || $[147] !== loanAsset || $[148] !== marketForRewards || $[149] !== marketId || $[150] !== t33) {
    t34 = <MarketSummaryCard marketId={marketId} current={t33} after={after} collateralAsset={collateralAsset} loanAsset={loanAsset} lltv={lltv} isLoading={isPositionLoading} marketForRewards={marketForRewards} avgBorrowApy={avgBorrowApy} />;
    $[142] = after;
    $[143] = avgBorrowApy;
    $[144] = collateralAsset;
    $[145] = isPositionLoading;
    $[146] = lltv;
    $[147] = loanAsset;
    $[148] = marketForRewards;
    $[149] = marketId;
    $[150] = t33;
    $[151] = t34;
  } else {
    t34 = $[151];
  }
  let t35;
  if ($[152] !== isBundleNotAuthorized) {
    t35 = isBundleNotAuthorized && <BundleAuthorizationCallout variant="destructive" message={MARKET_BUNDLE_NOT_AUTHORIZED_MESSAGE} />;
    $[152] = isBundleNotAuthorized;
    $[153] = t35;
  } else {
    t35 = $[153];
  }
  const t36 = isCollateralInputPending || isLoanInputPending;
  let t37;
  if ($[154] !== geoActionProps || $[155] !== handleFinalize || $[156] !== isDebouncing || $[157] !== t36) {
    t37 = <ActionButtons handleFinalize={handleFinalize} {...geoActionProps} isInputPending={t36} isValidating={isDebouncing} />;
    $[154] = geoActionProps;
    $[155] = handleFinalize;
    $[156] = isDebouncing;
    $[157] = t36;
    $[158] = t37;
  } else {
    t37 = $[158];
  }
  let t38;
  if ($[159] !== t25 || $[160] !== t29 || $[161] !== t34 || $[162] !== t35 || $[163] !== t37) {
    t38 = <>{t25}{t29}{t34}{t35}{t37}</>;
    $[159] = t25;
    $[160] = t29;
    $[161] = t34;
    $[162] = t35;
    $[163] = t37;
    $[164] = t38;
  } else {
    t38 = $[164];
  }
  return t38;
};
export default memo(MarketOperationSupplyBorrow);